import { ICreateNewsResponse } from 'shared/types/news.types';
import {
  TLoadOvertime,
  TOvertimeCreateRequest,
  TOvertimeGetByIdResponse,
  IOvertimeGetRequestParams,
  TOvertimeGetResponsePaginated,
  TOvertimeUpdateRequest,
  TOvertimeUserCreateRequest,
  TOvertimeUserUpdateRequest,
  TUserOvertimeInfoPaginated,
  TOvertimeTakeToWorkRequest,
  TUserOvertimeInfo,
  UsersToCheckRequestOvertime,
} from 'shared/types/overtime.types';
import { edpApiV1 } from './index';

export const overtimeApi = edpApiV1.injectEndpoints({
  endpoints: build => ({
    getOvertime: build.query<TOvertimeGetResponsePaginated, IOvertimeGetRequestParams>({
      query: params => ({ url: '/v1/admin/additional_income/overtime', method: 'GET', params }),
      providesTags: ['overtime'],
    }),
    getOvertimeById: build.query<TOvertimeGetByIdResponse, { id: number }>({
      query: params => ({ url: '/v1/admin/additional_income/overtimeById', method: 'GET', params }),
      providesTags: ['overtime'],
    }),
    updateOvertime: build.mutation<ICreateNewsResponse, TOvertimeUpdateRequest>({
      query: overtime => ({
        url: `/v1/admin/additional_income/overtime`,
        method: 'PATCH',
        body: overtime,
      }),
      invalidatesTags: ['overtime'],
    }),
    createOvertime: build.mutation<ICreateNewsResponse, TOvertimeCreateRequest>({
      query: body => ({
        url: `/v1/admin/additional_income/overtime`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['overtime'],
    }),
    deleteOvertime: build.mutation<string, { id: number }>({
      query: params => ({
        url: `/v1/admin/additional_income/overtime`,
        method: 'DELETE',
        params,
      }),
      invalidatesTags: ['overtime'],
    }),
    loadOvertime: build.mutation<ICreateNewsResponse, TLoadOvertime>({
      query: rvd => ({
        url: `/v1/admin/additional_income/status`,
        method: 'PATCH',
        body: rvd,
        responseHandler: response => response.text(),
      }),
      invalidatesTags: ['rvd'],
    }),
    createOvertimeUser: build.mutation<ICreateNewsResponse, TOvertimeUserCreateRequest>({
      query: body => ({
        url: `/v1/admin/additional_income/overtime_user`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['overtime_users', 'overtime'],
    }),
    updateOvertimeUser: build.mutation<ICreateNewsResponse, TOvertimeUserUpdateRequest>({
      query: body => ({
        url: `/v1/admin/additional_income/overtime_user`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['overtime_users', 'overtime'],
    }),
    deleteOvertimeUser: build.mutation<string, { id: number }>({
      query: params => ({
        url: `/v1/admin/additional_income/overtime_user`,
        method: 'DELETE',
        params,
      }),
      invalidatesTags: ['overtime_users', 'overtime'],
    }),
    getOvertimeUsers: build.query<
      TUserOvertimeInfoPaginated,
      { overtimeId: number; page: number; size: number; personnelNumber?: string }
    >({
      query: params => ({
        url: `/v1/admin/additional_income/overtime_user`,
        method: 'GET',
        params,
      }),
      providesTags: ['overtime_users'],
    }),
    overtimeTakeToWork: build.mutation<string, TOvertimeTakeToWorkRequest>({
      query: ({ hrId, overtimeId }) => {
        const body = hrId ? { hrId } : {};
        return {
          url: `/v1/admin/additional_income/overtime/${overtimeId}/take-to-work`,
          method: 'PATCH',
          body,
        };
      },
      invalidatesTags: ['overtime'],
    }),
    checkOvertimeUser: build.mutation<TUserOvertimeInfo[], UsersToCheckRequestOvertime>({
      query: body => ({
        url: `/v1/admin/additional_income/overtime_user/check`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['overtime_users', 'overtime'],
    }),
  }),
  overrideExisting: false,
});

export const {
  useCreateOvertimeMutation,
  useGetOvertimeQuery,
  useLazyGetOvertimeQuery,
  useUpdateOvertimeMutation,
  useGetOvertimeByIdQuery,
  useLazyGetOvertimeByIdQuery,
  useCreateOvertimeUserMutation,
  useDeleteOvertimeMutation,
  useDeleteOvertimeUserMutation,
  useUpdateOvertimeUserMutation,
  useGetOvertimeUsersQuery,
  useLazyGetOvertimeUsersQuery,
  useLoadOvertimeMutation,
  useOvertimeTakeToWorkMutation,
  useCheckOvertimeUserMutation,
} = overtimeApi;
